import { Routes } from '@angular/router';
import { authGuard } from './core/guards/auth.guard';
import { unsavedOpportunityGuard } from './core/guards/unsaved-opportunity.guard';
import { unsavedImportGuard } from './core/guards/unsaved-import.guard';

export const routes: Routes = [
  {
    path: 'login',
    loadComponent: () => import('./login/login').then((m) => m.Login),
  },
  {
    path: 'kiosk',
    loadComponent: () => import('./pages/time-clock/kiosk/kiosk').then((m) => m.Kiosk),
    title: 'Time Clock Kiosk',
  },
  {
    path: 'kiosk/setup',
    loadComponent: () => import('./pages/time-clock/kiosk-setup/kiosk-setup').then((m) => m.KioskSetup),
    title: 'Kiosk Setup',
  },
  {
    path: 'request-password-reset',
    loadComponent: () =>
      import('./pages/request-password-reset/request-password-reset').then(
        (m) => m.RequestPasswordReset,
      ),
  },
  {
    path: 'set-password',
    loadComponent: () => import('./pages/set-password/set-password').then((m) => m.SetPassword),
  },
  {
    path: 'forgot-password',
    loadComponent: () =>
      import('./pages/forgot-password/forgot-password').then((m) => m.ForgotPassword),
  },
  {
    // Where the console sends an operator to begin a maintenance session. Declared before the ''
    // Layout route on purpose: Layout's children include a '**' entry, so anything matched after
    // it would be swallowed and redirected to /login.
    //
    // Anonymous by necessity - the whole point is that the caller has no token yet. The one-time
    // code in the query string is the credential, and it is spent on arrival.
    path: 'enter',
    loadComponent: () => import('./enter/enter').then((m) => m.Enter),
    title: 'Opening session',
  },
  {
    path: '',
    loadComponent: () => import('./layout/layout').then((m) => m.Layout),
    children: [
      {
        path: '',
        redirectTo: 'dashboard',
        pathMatch: 'full',
      },
      {
        path: 'demo',
        loadComponent: () => import('./demo/demo').then((m) => m.Demo),
        title: 'Shift Guard Demo',
      },
      {
        path: 'dashboard',
        loadComponent: () => import('./pages/dashboard/dashboard').then((m) => m.Dashboard),
        title: 'Shift Guard Dashboard',
        canActivate: [authGuard],
        data: { features: ['dashboard'] }, // Accessible to all logged-in users
      },
      {
        path: 'mopinterval-simulation',
        loadComponent: () => import('./pages/mopinterval/mopinterval').then((m) => m.Mopinterval),
        title: 'MOpInterval Simulation',
        canActivate: [authGuard],
        data: { features: ['dashboard'] }, // Accessible via dashboard feature
      },
      {
        path: 'notifications',
        loadComponent: () =>
          import('./pages/notifications/notifications').then((m) => m.Notifications),
        title: 'Notifications',
        canActivate: [authGuard],
        data: { features: ['notifications'] },
      },
      {
        path: 'users',
        loadComponent: () => import('./users/users').then((m) => m.Users),
        title: 'Shift Guard Users',
        canActivate: [authGuard],
        data: { features: ['users'] },
      },
      {
        path: 'users/create',
        loadComponent: () => import('./createuser/createuser').then((m) => m.Createuser),
        title: 'Shift Guard Users',
        canActivate: [authGuard],
        data: { features: ['user_create'] },
      },
      {
        path: 'users/edit/:id',
        loadComponent: () => import('./createuser/createuser').then((m) => m.Createuser),
        canActivate: [authGuard],
        data: { features: ['user_edit'] },
      },
      {
        path: 'roles',
        loadComponent: () => import('./roles/roles').then((m) => m.Roles),
        canActivate: [authGuard],
        data: { features: ['roles'] },
      },
      {
        path: 'roles/create',
        loadComponent: () => import('./createrole/createrole').then((m) => m.Createrole),
        canActivate: [authGuard],
        data: { features: ['role_create'] },
      },
      {
        path: 'roles/edit/:id',
        loadComponent: () => import('./createrole/createrole').then((m) => m.Createrole),
        canActivate: [authGuard],
        data: { features: ['role_edit'] },
      },
      {
        path: 'teams',
        loadComponent: () => import('./pages/teams/teams').then((m) => m.Teams),
        title: 'Teams',
        canActivate: [authGuard],
        data: { features: ['teams'] },
      },
      {
        path: 'teams/create',
        loadComponent: () => import('./pages/create-team/create-team').then((m) => m.CreateTeam),
        title: 'Create Team',
        canActivate: [authGuard],
        data: { features: ['team_create'] },
      },
      {
        path: 'teams/edit/:id',
        loadComponent: () => import('./pages/create-team/create-team').then((m) => m.CreateTeam),
        title: 'Edit Team',
        canActivate: [authGuard],
        data: { features: ['team_edit'] },
      },
      {
        path: 'skills',
        loadComponent: () => import('./pages/skills/skills').then((m) => m.Skills),
        title: 'Skills',
        canActivate: [authGuard],
        data: { features: ['teams'] },
      },
      {
        path: 'skills/create',
        loadComponent: () => import('./pages/create-skill/create-skill').then((m) => m.CreateSkill),
        title: 'Create Skill',
        canActivate: [authGuard],
        data: { features: ['teams'] },
      },
      {
        path: 'skills/edit/:id',
        loadComponent: () => import('./pages/create-skill/create-skill').then((m) => m.CreateSkill),
        title: 'Edit Skill',
        canActivate: [authGuard],
        data: { features: ['teams'] },
      },
      {
        path: 'ai-schedules',
        loadComponent: () => import('./pages/aischedules/aischedules').then((m) => m.Aischedules),
        title: 'Shift Guard Schedules',
        canActivate: [authGuard],
        data: { features: ['ai-schedules'] },
      },
      {
        path: 'ai-schedules/create-simple',
        loadComponent: () =>
          import('./pages/simple-schedule-generator/simple-schedule-generator').then(
            (m) => m.SimpleScheduleGenerator,
          ),
        title: 'Generate Schedule (Simple)',
        canActivate: [authGuard],
        data: { features: ['ai-schedules'] },
      },
      {
        path: 'ai-schedule/manager/:id',
        loadComponent: () =>
          import('./pages/manager-calander-view/manager-calander-view').then(
            (m) => m.ManagerCalanderView,
          ),
        title: 'Schedule Manager View',
        canActivate: [authGuard],
        data: { features: ['schedule_edit', 'ai-schedules'] },
      },
      {
        path: 'ai-schedule/details/:id',
        loadComponent: () =>
          import('./pages/schedule-details/schedule-details').then((m) => m.ScheduleDetails),
        title: 'Shift Guard Schedule Details',
        canActivate: [authGuard],
        data: { features: ['schedule_details'] },
      },
      {
        path: 'incidents',
        loadComponent: () => import('./pages/incidents/incidents').then((m) => m.Incidents),
        title: 'Shift Guard Incidents',
        canActivate: [authGuard],
        data: { features: ['incidents'] },
      },
      {
        path: 'rulesets',
        loadComponent: () => import('./pages/rulesets/rulesets').then((m) => m.Rulesets),
        title: 'Compliance Rulesets',
        canActivate: [authGuard],
        data: { features: ['rulesets'] },
      },
      {
        path: 'rulesets/create',
        loadComponent: () =>
          import('./pages/create-ruleset/create-ruleset').then((m) => m.CreateRuleset),
        title: 'Create Compliance Ruleset',
        canActivate: [authGuard],
        data: { features: ['ruleset_create'] },
      },
      {
        path: 'rulesets/edit/:id',
        loadComponent: () =>
          import('./pages/create-ruleset/create-ruleset').then((m) => m.CreateRuleset),
        title: 'Edit Compliance Ruleset',
        canActivate: [authGuard],
        data: { features: ['ruleset_edit'] },
      },
      {
        path: 'regions',
        loadComponent: () => import('./pages/regions/regions').then((m) => m.Regions),
        title: 'Regions',
        canActivate: [authGuard],
        data: { features: ['regions'] },
      },
      {
        path: 'regions/create',
        loadComponent: () =>
          import('./pages/create-region/create-region').then((m) => m.CreateRegion),
        title: 'Create Region',
        canActivate: [authGuard],
        data: { features: ['region_create'] },
      },
      {
        path: 'regions/edit/:id',
        loadComponent: () =>
          import('./pages/create-region/create-region').then((m) => m.CreateRegion),
        title: 'Edit Region',
        canActivate: [authGuard],
        data: { features: ['region_edit'] },
      },
      {
        path: 'regions/:id/rulesets',
        loadComponent: () =>
          import('./pages/region-rulesets/region-rulesets').then((m) => m.RegionRulesets),
        title: 'Region Rulesets',
        canActivate: [authGuard],
        data: { features: ['regions'] },
      },
      {
        // Opportunities. Implements the pricing workbook sheet by sheet.
        path: 'opportunities',
        loadComponent: () => import('./pages/workbook/workbook').then((m) => m.Workbook),
        title: 'Opportunities',
        canActivate: [authGuard],
        data: { features: ['opportunity_view'] },
      },
      {
        // The company's list of things to look for in an RFP. Declared before
        // opportunities/:id, which would otherwise match "checklist" as an opportunity id
        // and send the reader to a 404 that looks like a missing record.
        path: 'opportunities/checklist',
        loadComponent: () =>
          import('./pages/rfp-checklist/rfp-checklist').then((m) => m.RfpChecklist),
        title: 'RFP checklist',
        canActivate: [authGuard],
        data: { features: ['opportunity_view'] },
      },
      {
        // What a generated proposal may claim about this company. Before opportunities/:id
        // for the same reason as the checklist: that route would match the word as an id.
        path: 'opportunities/company-profile',
        loadComponent: () =>
          import('./pages/company-profile/company-profile').then((m) => m.CompanyProfile),
        title: 'Company profile',
        canActivate: [authGuard],
        data: { features: ['opportunity_view'] },
      },
      {
        // One opportunity, open. Its own route rather than a mode of the list: it is what
        // gives the breadcrumb something to go back to, makes the browser's back button
        // mean "the list" instead of "leave the page", and makes an open opportunity a
        // link somebody can send.
        path: 'opportunities/:id',
        loadComponent: () => import('./pages/workbook/workbook').then((m) => m.Workbook),
        title: 'Opportunity',
        canActivate: [authGuard],
        // Every way out goes through here — Close, the breadcrumb, the back button and the
        // sidebar — so an unnamed opportunity is asked about once, wherever you leave from.
        canDeactivate: [unsavedOpportunityGuard],
        data: { features: ['opportunity_view'] },
      },
      {
        // The earlier twelve-step wizard. Reachable by URL so nothing already pointing at
        // it breaks, but no longer in the menu.
        path: 'opportunities-wizard',
        loadComponent: () =>
          import('./pages/opportunities/opportunities').then((m) => m.Opportunities),
        title: 'Opportunities (wizard)',
        canActivate: [authGuard],
        data: { features: ['opportunity_view'] },
      },
      {
        path: 'data-import',
        loadComponent: () => import('./pages/data-import/data-import').then((m) => m.DataImport),
        title: 'Data Import',
        canActivate: [authGuard],
        canDeactivate: [unsavedImportGuard],
        data: { features: ['data_import'] },
      },
      {
        path: 'audit-trail',
        loadComponent: () => import('./pages/audit-trail/audit-trail').then((m) => m.AuditTrail),
        title: 'Audit Trail',
        canActivate: [authGuard],
        data: { features: ['audit-trail'] },
      },
      {
        path: 'reports/violations',
        loadComponent: () =>
          import('./pages/reports/violations/violations').then((m) => m.ViolationsReport),
        title: 'Adherence Instances Report',
        canActivate: [authGuard],
        data: { features: ['report_violations'] },
      },
      {
        path: 'reports/compliance-summary',
        loadComponent: () =>
          import('./pages/reports/compliance-summary/compliance-summary').then(
            (m) => m.ComplianceSummary,
          ),
        title: 'Compliance Summary',
        canActivate: [authGuard],
        data: { features: ['report_compliance_summary'] },
      },
      {
        path: 'reports/adherence',
        loadComponent: () =>
          import('./pages/reports/adherence/adherence').then((m) => m.AdherenceReport),
        title: 'Adherence Report',
        canActivate: [authGuard],
        data: { features: ['report_adherence'] },
      },
      {
        path: 'reports/remediation',
        loadComponent: () =>
          import('./pages/reports/remediation/remediation').then((m) => m.RemediationReport),
        title: 'Remediation Report',
        canActivate: [authGuard],
        data: { features: ['report_remediation'] },
      },
      {
        path: 'reports/meal-break-compliance',
        loadComponent: () =>
          import('./pages/reports/meal-break-compliance/meal-break-compliance').then(
            (m) => m.MealBreakComplianceReport,
          ),
        title: 'Meal Break Compliance',
        canActivate: [authGuard],
        data: { features: ['report_meal_break_compliance'] },
      },
      {
        path: 'profile',
        loadComponent: () => import('./pages/user-profile/user-profile').then((m) => m.UserProfile),
        title: 'Shift Guard User Profile',
        canActivate: [authGuard],
        data: { features: ['profile'] },
      },
      {
        path: 'schedule',
        loadComponent: () => import('./schedule/schedule').then((m) => m.Schedule),
        title: 'Project Schedule',
        canActivate: [authGuard],
        data: { features: ['project-schedule'] },
      },
      // ===== TIME KEEPING MODULE =====
      {
        path: 'attendance',
        loadComponent: () =>
          import('./pages/time-clock/attendance-dashboard/attendance-dashboard').then(
            (m) => m.AttendanceDashboard,
          ),
        title: 'Attendance Dashboard',
        canActivate: [authGuard],
        data: { features: ['attendance'] },
      },
      {
        path: 'time-clock/wfh',
        loadComponent: () =>
          import('./pages/time-clock/wfh-clock/wfh-clock').then((m) => m.WfhClock),
        title: 'WFH Time Clock',
        canActivate: [authGuard],
        data: { features: ['timeclock_wfh'] },
      },
      {
        path: 'time-clock/config',
        loadComponent: () =>
          import('./pages/time-clock/timeclock-config/timeclock-config').then(
            (m) => m.TimeClockConfig,
          ),
        title: 'Time Clock Configuration',
        canActivate: [authGuard],
        data: { features: ['timeclock_config'] },
      },
      {
        path: 'time-clock/devices',
        loadComponent: () =>
          import('./pages/time-clock/device-management/device-management').then(
            (m) => m.DeviceManagement,
          ),
        title: 'Device Management',
        canActivate: [authGuard],
        data: { features: ['timeclock_devices'] },
      },
      {
        path: 'time-clock/events',
        loadComponent: () =>
          import('./pages/time-clock/clock-events/clock-events').then(
            (m) => m.ClockEvents,
          ),
        title: 'Clock In & Out Events Logs',
        canActivate: [authGuard],
        data: { features: ['timeclock_events'] },
      },
      {
        path: 'time-clock/attendance-records',
        loadComponent: () =>
          import('./pages/time-clock/attendance-records/attendance-records').then(
            (m) => m.AttendanceRecords,
          ),
        title: 'Clock In & Out Events',
        canActivate: [authGuard],
        data: { features: ['attendance'] },
      },
    ],
  },
  // Top level, not nested inside Layout. Nested, it only matched paths that had already been
  // routed into Layout, and it shadowed every sibling route declared after it.
  {
    path: '**',
    redirectTo: '/login',
  },
];
